import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, refreshToken, AUTH_LOGOUT_EVENT, TOKEN_DIGANTI_EVENT } from "../lib/api";
import ForceChangePasswordModal from "../components/ForceChangePasswordModal";

const AuthContext = createContext(null);
const WELCOME_KEY = "epplehub_login_welcome";
function storedWelcome() {
  try { return JSON.parse(sessionStorage.getItem(WELCOME_KEY) || "null"); } catch { return null; }
}
function persistWelcome(value) {
  try { if (value) sessionStorage.setItem(WELCOME_KEY, JSON.stringify(value)); else sessionStorage.removeItem(WELCOME_KEY); } catch { /* Private browsing may disable storage. */ }
}

// Kunci `inventory_*` yang TIDAK ikut master toggle `inventory` maupun bawaan
// role finance_* — cermin `deps.py::KUNCI_INVENTORY_KHUSUS` (brief 22 Sep 2026
// G3: pengisi info unit dicentang per akun). Kalau cermin ini tertinggal, tombol
// "Isi info unit" tampil untuk tim Finance lalu berujung 403 (aturan #3).
// Dijaga `tests/test_price_list_imei_info_unit.py`.
const KUNCI_INVENTORY_KHUSUS = ["inventory_info_unit"];

// Role-based default permissions (client-side mirror of backend)
// Mirrors authorize_module role defaults for finance_* roles.
function defaultCan(role, module, action) {
  if (role === "superadmin") return true;
  if (role === "admin") return true;
  if (role === "viewer") return action === "view";
  // Finance family
  if (typeof role === "string" && role.startsWith("finance_")) {
    // Backend (authorize_module di deps.py) sengaja memberi role finance_* akses setara di
    // modul Inventory — stok adalah uang, dikelola tim yang sama. Selama salinan aturan di
    // sini tidak menyalin bagian itu, menu Inventory tidak pernah muncul untuk tim Finance
    // walaupun API-nya meloloskan mereka: fiturnya ada tapi tak terlihat.
    const isFinanceMod = module === "finance" || module === "inventory"
      || (typeof module === "string" && (module.startsWith("finance_")
        || (module.startsWith("inventory_") && !KUNCI_INVENTORY_KHUSUS.includes(module))));
    if (isFinanceMod) {
      if (role === "finance_auditor") return action === "view";
      if (role === "finance_staff") return ["view", "create", "update"].includes(action);
      return true; // finance_spv, finance_ceo, other → full
    }
    // Modul non-finance: default deny (user rule: tidak dicentang = tidak muncul)
    return false;
  }
  return false;
}

// Izin RBAC berjangka waktu (brief owner poin 13: "edit faktur 5 menit, lewat
// itu terkunci otomatis"). Mirror dari `_aksi_aktif()` di backend/deps.py —
// baris izin BOLEH punya `row.expires[action]` (ISO8601 tz-aware). Ini HANYA
// dipakai untuk menyembunyikan tombol lebih cepat di client; gerbang yang
// SESUNGGUHNYA selalu di backend (authorize_module dicek ulang tiap request
// dari DB, bukan dari token) — jadi kalaupun jam perangkat user meleset,
// yang terjadi paling buruk adalah tombol telat/cepat hilang beberapa detik,
// BUKAN lubang keamanan (endpoint tetap menolak sesuai waktu SERVER).
// Diekspor (bukan cuma dipakai `can()` internal) supaya halaman yang perlu
// gerbang LEBIH SEMPIT daripada default role generik (mis.
// `pages/finance/FinancePettyCash.jsx` — approve/reject/hapus sengaja lebih
// sempit daripada "update" biasa) bisa membangun cek izin eksplisit sendiri
// tanpa menyalin ulang logika expiry ini.
export function isPermissionActionExpired(row, action) {
  if (!row || typeof row !== "object") return false;
  const exp = row.expires && row.expires[action];
  if (!exp) return false;
  const t = Date.parse(exp);
  if (Number.isNaN(t)) return false;
  return Date.now() >= t;
}

// Portal Karyawan auto-grant: mirror server logic (needs user context, exposed via useAuth.can())
function myPortalAutoGrant(user, module, action) {
  if (!user?.employee_id) return false;
  if (typeof module !== "string" || !module.startsWith("my_")) return false;
  if (module === "my_attendance") return ["view", "create"].includes(action);
  if (module === "my_cuti") return ["view", "create", "update"].includes(action);
  if (module === "my_profile") return ["view", "update"].includes(action);
  return action === "view"; // my_slip, my_kpi read-only
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [welcomeSession, setWelcomeSession] = useState(storedWelcome);
  const finishWelcome = useCallback(() => { persistWelcome(null); setWelcomeSession(null); }, []);
  // Satu-satunya pemanggil GET /config/branding di seluruh app (brief bos, laporan
  // performa): dulu Layout.jsx dan FinanceLayout.jsx masing-masing memanggil endpoint
  // yang sama saat mount, jadi tiap muat halaman = 2-3 request identik. Sekarang
  // mereka mengonsumsi state ini lewat useAuth() dan request-nya tinggal satu.
  const [branding, setBranding] = useState({
    app_name: "EppleHub Operational System", header_logo_url: "",
    footer_text: "© 2026 EppleHub. All rights reserved.", footer_logo_url: "", show_footer: true, nama_toko_sendiri: "EppleHub",
  });

  // Load branding config on mount → set browser tab title + favicon + OG meta + manifest dinamis
  useEffect(() => {
    const upsertMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const upsertLink = (rel, href, extraAttrs = {}) => {
      // Hapus semua link rel=rel yang ada, lalu tambah baru
      document.querySelectorAll(`link[rel~='${rel}']`).forEach(el => el.remove());
      const link = document.createElement("link");
      link.rel = rel;
      link.href = href;
      Object.entries(extraAttrs).forEach(([k, v]) => link.setAttribute(k, v));
      document.head.appendChild(link);
    };
    const applyBrandingToTab = (b) => {
      // 1. Title
      if (b?.browser_title) document.title = b.browser_title;
      // 2. Favicon + Apple Touch Icon (pakai favicon yang sama, browser auto-scale ke 180x180)
      if (b?.favicon_url) {
        const type = b.favicon_url.startsWith("data:image/svg") ? "image/svg+xml"
                   : b.favicon_url.startsWith("data:image/png") ? "image/png"
                   : "image/x-icon";
        upsertLink("icon", b.favicon_url, { type });
        upsertLink("apple-touch-icon", b.favicon_url);
      }
      // 3. Open Graph / Twitter Card
      const ogTitle = b?.browser_title || b?.app_name;
      const ogDesc = b?.og_description;
      const ogImage = b?.og_image_url || b?.favicon_url;
      upsertMeta("property", "og:title", ogTitle);
      upsertMeta("property", "og:description", ogDesc);
      upsertMeta("property", "og:image", ogImage);
      upsertMeta("property", "og:type", "website");
      upsertMeta("name", "twitter:card", "summary_large_image");
      upsertMeta("name", "twitter:title", ogTitle);
      upsertMeta("name", "twitter:description", ogDesc);
      upsertMeta("name", "twitter:image", ogImage);
      upsertMeta("name", "description", ogDesc);
      // 4. Dynamic PWA Manifest (build JSON in-memory, serve via Blob URL)
      const pwaName = b?.pwa_name || b?.app_name || "EppleHub Operational System";
      const pwaShort = b?.pwa_short_name || pwaName.slice(0, 12);
      const manifest = {
        name: pwaName, short_name: pwaShort,
        description: ogDesc || "Sistem Informasi EppleHub",
        start_url: "/", display: "standalone",
        background_color: "#ffffff", theme_color: "#059669",
        icons: b?.favicon_url ? [
          { src: b.favicon_url, sizes: "192x192", type: "image/png", purpose: "any" },
          { src: b.favicon_url, sizes: "512x512", type: "image/png", purpose: "any maskable" },
        ] : [],
      };
      const blob = new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" });
      const url = URL.createObjectURL(blob);
      upsertLink("manifest", url);
    };
    api.get("/config/branding").then(r => {
      applyBrandingToTab(r.data);
      setBranding(b => ({ ...b, ...r.data }));
    }).catch(() => {});
    const handler = (e) => {
      applyBrandingToTab(e.detail);
      setBranding(b => ({ ...b, ...e.detail }));
    };
    window.addEventListener("branding-updated", handler);
    return () => window.removeEventListener("branding-updated", handler);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("hris_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then(r => setUser(r.data))
      .catch((err) => {
        // HANYA hapus token kalau server konfirmasi 401 (Unauthorized).
        // Jangan force logout kalau network error / timeout / 5xx — biarkan user
        // tetap login, aksi bisa retry saat koneksi kembali.
        if (err?.response?.status === 401) {
          localStorage.removeItem("hris_token");
        }
        // Untuk error lain: JANGAN clear token, JANGAN logout.
      })
      .finally(() => setLoading(false));
  }, []);

  // Listen ke event 401 dari axios response interceptor (di api.js)
  // → clear user state + redirect ke /login. Token sudah dihapus oleh interceptor.
  useEffect(() => {
    const handler = () => {
      setUser(null);
      persistWelcome(null); setWelcomeSession(null);
      // Redirect ke /login kalau belum di sana (avoid infinite loop)
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    };
    window.addEventListener(AUTH_LOGOUT_EVENT, handler);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handler);
  }, []);

  // Refresh tetap memakai jangkar login; tidak melewati pukul 00.00 WIB.
  useEffect(() => {
    if (!user) return;
    const REFRESH_MS = 6 * 60 * 60 * 1000; // 6 jam
    const timer = setInterval(() => { refreshToken(); }, REFRESH_MS);
    // Juga refresh sekali saat user pertama loaded (kalau tab dibuka sesudah lama tidak aktif)
    refreshToken();
    return () => clearInterval(timer);
  }, [user]);

  // Segarkan `/auth/me` lebih rapat (bukan tiap 6 jam) SELAMA user ini punya
  // izin berjangka waktu yang sedang berjalan (poin 13) — GET /auth/me selalu
  // membaca izin dari DB (deps.get_current_user), jadi ini satu-satunya cara
  // frontend tahu box mana yang baru kedaluwarsa (atau diperpanjang/dicabut
  // superadmin) tanpa menunggu user logout-login. `can()` di atas SUDAH
  // menolak sendiri begitu jam lokal melewati `expires` (tidak perlu menunggu
  // fetch ini) — polling ini murni supaya badge sisa waktu & kondisi lain
  // (revoke dini, perpanjangan) ikut ter-refresh, dan otomatis BERHENTI begitu
  // tidak ada lagi grant yang aktif/baru saja berakhir (lihat jeda 2 menit di
  // bawah) supaya user TANPA izin sementara (mayoritas) tidak ikut kena beban
  // polling ini sama sekali.
  useEffect(() => {
    if (!user?.permissions) return;
    const now = Date.now();
    const GRACE_MS = 2 * 60 * 1000; // beri jeda 2 menit setelah lewat supaya UI sempat menangkap transisinya
    const adaGrantAktif = Object.values(user.permissions).some((row) => {
      if (!row || typeof row !== "object" || !row.expires) return false;
      return Object.values(row.expires).some((ts) => {
        const t = Date.parse(ts);
        return !Number.isNaN(t) && t > now - GRACE_MS;
      });
    });
    if (!adaGrantAktif) return;
    const POLL_MS = 20 * 1000; // 20 detik — cukup rapat untuk sesi berjangka menit-an
    const timer = setInterval(() => {
      api.get("/auth/me").then(r => setUser(r.data)).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [user]);

  const login = async (identitas, password) => {
    // Redesign 24 Agu 2026: login utama per-USERNAME. Input ber-"@" tetap
    // dikirim sebagai email (jalan superadmin — hanya dia yang tahu email).
    const body = identitas.includes("@")
      ? { email: identitas, password }
      : { username: identitas, password };
    const { data } = await api.post("/auth/login", body);
    // Buang cache React Query milik sesi sebelumnya SEBELUM user baru dipasang.
    // Login berpindah lewat router (nav), bukan reload penuh, jadi cache lama
    // bertahan di memori — tanpa ini pengguna berikutnya di tab yang sama bisa
    // sekilas melihat data pengguna sebelumnya. Ini jaring pengaman terakhir:
    // doLogout di Layout.jsx & FinanceLayout.jsx sudah membersihkan saat keluar,
    // tapi sesi bisa juga berakhir tanpa lewat tombol itu.
    queryClient.clear();
    localStorage.setItem("hris_token", data.token);
    const nextWelcome = { userId: data.user.id, startedAt: new Date().toISOString() };
    persistWelcome(nextWelcome); setWelcomeSession(nextWelcome);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("hris_token");
    finishWelcome();
    setUser(null);
  };

  // Ganti password sendiri menerbitkan token bersesi baru (exp baru) tanpa
  // login ulang — timer sesi di bawah harus dihitung ulang dari token itu,
  // kalau tidak perangkat ini tetap dikeluarkan pada batas sesi yang lama.
  const [tokenDiganti, setTokenDiganti] = useState(0);
  useEffect(() => {
    const handler = () => setTokenDiganti((n) => n + 1);
    window.addEventListener(TOKEN_DIGANTI_EVENT, handler);
    return () => window.removeEventListener(TOKEN_DIGANTI_EVENT, handler);
  }, []);

  // Logout 00.00 WIB, juga untuk token lama dengan exp 24 jam. Tab yang tidur
  // diperiksa lagi saat dibuka; server tetap menegakkan batas yang sama.
  useEffect(() => {
    const token = localStorage.getItem("hris_token");
    if (!user || !token) return;
    let deadline;
    try {
      const klaim = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      const start = klaim.sesi_mulai ? Date.parse(klaim.sesi_mulai) : klaim.iat * 1000;
      const wibOffset = 7 * 60 * 60 * 1000;
      const midnight = (Math.floor((start + wibOffset) / 86400000) + 1) * 86400000 - wibOffset;
      deadline = Math.min(klaim.exp * 1000, midnight);
    } catch { return; }
    if (!Number.isFinite(deadline)) return;
    const expire = () => {
      if (Date.now() < deadline) return;
      localStorage.removeItem("hris_token");
      window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT, { detail: { reason: "sesi-tengah-malam-wib" } }));
    };
    const t = setTimeout(expire, Math.max(0, Math.min(deadline - Date.now(), 2147000000)));
    window.addEventListener("focus", expire);
    document.addEventListener("visibilitychange", expire);
    return () => {
      clearTimeout(t);
      window.removeEventListener("focus", expire);
      document.removeEventListener("visibilitychange", expire);
    };
  }, [user, tokenDiganti]);

  /** Client-side authz check. Mirrors server's `authorize_module`.
   *  Superadmin bypasses. Explicit user.permissions[module][action] wins.
   *  Legacy alias: finance.action grants finance_*.action if sub not explicitly set.
   *  Else fall back to role defaults.
   *  Izin berjangka waktu (poin 13): setiap cabang di bawah juga menolak kalau
   *  `row.expires[action]` sudah lewat — lihat `isPermissionActionExpired()`. */
  const can = (module, action = "view") => {
    if (!user) return false;
    if (user.role === "superadmin") return true;
    const perms = user.permissions || {};
    if (perms[module] && Object.prototype.hasOwnProperty.call(perms[module], action)) {
      return !!perms[module][action] && !isPermissionActionExpired(perms[module], action);
    }
    // Legacy finance master toggle grants all finance_* subs if not explicitly set
    if (typeof module === "string" && module.startsWith("finance_")) {
      const legacy = perms.finance;
      if (legacy && Object.prototype.hasOwnProperty.call(legacy, action) && legacy[action]
          && !isPermissionActionExpired(legacy, action)) {
        return true;
      }
    }
    // Master toggle `inventory` melonggarkan semua inventory_* di backend (deps.py). Tanpa
    // baris ini, admin yang mencentang satu kotak "Inventory" melihat menunya tetap kosong
    // padahal API-nya sudah mengizinkan — user menyangka izinnya gagal tersimpan.
    if (typeof module === "string" && module.startsWith("inventory_")
        && !KUNCI_INVENTORY_KHUSUS.includes(module)) {
      const master = perms.inventory;
      if (master && Object.prototype.hasOwnProperty.call(master, action) && master[action]
          && !isPermissionActionExpired(master, action)) {
        return true;
      }
    }
    // Master toggle `marketing` — sama seperti inventory di atas. HARUS mengecualikan
    // `marketing_margin` persis seperti backend (deps.py::authorize_module): kunci itu
    // menentukan boleh-tidaknya melihat margin/HPP/ROI, dan owner sengaja memisahkannya
    // supaya Admin Marketing bisa mengelola seluruh modul TANPA pernah melihat margin.
    // Kalau di sini ikut dilonggarkan, layar akan menyiapkan kolom margin yang datanya
    // memang tidak pernah dikirim backend — kolom kosong yang membingungkan.
    if (typeof module === "string" && module.startsWith("marketing_")
        && module !== "marketing_margin") {
      const master = perms.marketing;
      if (master && Object.prototype.hasOwnProperty.call(master, action) && master[action]
          && !isPermissionActionExpired(master, action)) {
        return true;
      }
    }
    // Portal Karyawan auto-grant for anyone linked to an employee record
    if (myPortalAutoGrant(user, module, action)) return true;
    return defaultCan(user.role, module, action);
  };

  /** Return data scope for a module: "all", "cabang", atau "own".
   *  Admin/superadmin always "all". Others honor perms[module].scope, default "own".
   *
   *  JANGAN menulis `scope(m) === "own" ? <sempit> : <semua>`. Sejak 21 Agu 2026
   *  ada nilai ketiga "cabang", dan ia BUKAN "own" — pola dua cabang seperti itu
   *  memperlakukannya sebagai "semua", sehingga pemegang lingkup paling ketat
   *  justru mendapat tampilan paling luas. Tangani ketiganya eksplisit.
   *  Nilai yang tidak dikenal sengaja jatuh ke "own" (paling ketat), bukan "all". */
  const scope = (module) => {
    if (!user) return "own";
    if (user.role === "superadmin" || user.role === "admin") return "all";
    const perms = user.permissions || {};
    const s = perms[module]?.scope;
    return s === "all" || s === "cabang" ? s : "own";
  };

  /** Return whether a dashboard widget key is visible for this user.
   *  Admin/superadmin: all visible. If user has explicit dashboard_widgets list (array),
   *  only those keys are shown. If list is null/undefined, all visible by default. */
  const canWidget = (widgetKey) => {
    if (!user) return false;
    if (user.role === "superadmin" || user.role === "admin") return true;
    const list = user.dashboard_widgets;
    if (!Array.isArray(list)) return true; // no filter set
    if (list.includes(widgetKey)) return true;
    // Akun lama mungkin masih menyimpan key sebelum widget omzet dipecah.
    return widgetKey === "sales_omzet_total" && list.includes("sales_mtd");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, can, scope, canWidget, setUser, branding, welcomeSession, finishWelcome }}>
      {children}
      {user?.must_change_password && (
        <ForceChangePasswordModal onDone={() => setUser({ ...user, must_change_password: false })} />
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
